import { useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import {
  alignLeadsListContextToLeadId,
  buildLeadsListPath,
  canGoToNextLead,
  canGoToPreviousLead,
  getLeadsListGlobalIndex,
  getLeadsNavigationState,
  navigateToLeadDetails,
  resolveAdjacentLead,
} from "./leadDetailsNavigation"

export function useLeadListNavigation(leadSource, leadId) {
  const location = useLocation()
  const navigate = useNavigate()
  const axios = useAuthorizedAxiosClient()
  const [isNavigatingAdjacent, setIsNavigatingAdjacent] = useState(false)
  const [adjacentNavigationError, setAdjacentNavigationError] = useState(false)

  const rawContext = leadSource
    ? getLeadsNavigationState(location, leadSource).listContext
    : null
  const listContext = alignLeadsListContextToLeadId(rawContext, leadId)
  const globalIndex = getLeadsListGlobalIndex(listContext)
  const positionLabel =
    listContext && globalIndex != null
      ? `${globalIndex} of ${listContext.totalCount}`
      : null

  async function goToAdjacent(direction) {
    if (!listContext || isNavigatingAdjacent) {
      return
    }

    setAdjacentNavigationError(false)
    setIsNavigatingAdjacent(true)
    try {
      const result = await resolveAdjacentLead(axios, listContext, direction)
      if (!result) {
        setAdjacentNavigationError(true)
        return
      }
      navigateToLeadDetails(
        navigate,
        listContext.leadSource,
        result.leadId,
        result.listContext
      )
    } catch {
      setAdjacentNavigationError(true)
    } finally {
      setIsNavigatingAdjacent(false)
    }
  }

  return {
    canGoPrevious: canGoToPreviousLead(listContext),
    canGoNext: canGoToNextLead(listContext),
    hasListContext: !!listContext,
    positionLabel,
    isNavigatingAdjacent,
    adjacentNavigationError,
    goBackToList: () => navigate(buildLeadsListPath(leadSource)),
    goToPrevious: () => goToAdjacent("prev"),
    goToNext: () => goToAdjacent("next"),
  }
}
